import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { SERVICES } from "@/lib/site";

const FEATURE_IMG =
  "https://images.unsplash.com/photo-1598408745613-178751e2ccde?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxtYXJpbmUlMjB2ZXNzZWwlMjBvY2VhbnxlbnwwfHx8fDE3ODU3NjYyNDV8MA&ixlib=rb-4.1.0&q=85";

const fade = {
  hidden: { opacity: 0, y: 40 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: i * 0.1 },
  }),
};

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative bg-[#0A111D] py-24 lg:py-36 border-y border-white/10"
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          {/* Feature image with spotlight framing */}
          <motion.div
            variants={fade}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="lg:col-span-5 relative overflow-hidden clip-slant min-h-[420px]"
            data-testid="services-feature"
          >
            <img
              src={FEATURE_IMG}
              alt="Marine vessel served by JP Energy Solution"
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-[#050B14]/40" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050B14] via-transparent to-transparent" />
            <div className="relative z-10 h-full flex flex-col justify-end p-8 lg:p-10">
              <span className="font-mono-tech text-xs uppercase tracking-[0.3em] text-[#FF5A00]">
                Sectors We Serve
              </span>
              <p className="mt-4 font-serif-display text-2xl lg:text-3xl text-[#F8FAFC] leading-snug max-w-sm">
                From turbine to bridge — electrical integrity across every offshore asset.
              </p>
            </div>
          </motion.div>

          {/* Service list */}
          <div className="lg:col-span-7 flex flex-col">
            <motion.div
              variants={fade}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="mb-8"
            >
              <h2 className="font-serif-display text-4xl sm:text-5xl leading-[1.05] text-[#F8FAFC]">
                Where we operate.
              </h2>
            </motion.div>

            <div className="flex flex-col divide-y divide-white/10 border-t border-white/10">
              {SERVICES.map((s, i) => (
                <motion.div
                  key={s.title}
                  custom={i}
                  variants={fade}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true }}
                  data-testid={`service-card-${i}`}
                  className="group grid grid-cols-1 sm:grid-cols-12 gap-4 py-8 hover:bg-[#050B14] transition-colors duration-300 px-2 -mx-2"
                >
                  <div className="sm:col-span-3">
                    <span className="inline-block font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#FFC107] border border-[#FFC107]/40 px-3 py-1">
                      {s.tag}
                    </span>
                  </div>
                  <div className="sm:col-span-9 flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-serif-display text-2xl text-[#F8FAFC] group-hover:text-[#FF5A00] transition-colors duration-300">
                        {s.title}
                      </h3>
                      <p className="mt-2 font-mono-tech text-sm text-[#94A3B8] leading-relaxed max-w-md">
                        {s.body}
                      </p>
                    </div>
                    <ArrowUpRight className="h-6 w-6 shrink-0 text-[#94A3B8] group-hover:text-[#FF5A00] group-hover:-translate-y-1 group-hover:translate-x-1 transition-all duration-300" />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
